/// <reference path="../../../app.definition.ts" />

import * as angular from "angular";
import * as pdfjsLib from "pdfjs-dist";
import * as pdfjsViewer from "pdfjs-dist/web/pdf_viewer.mjs";
import { JavaApiAdapterService } from "@common/apiadapter/javaapi.service";
import { PopUpService } from "@common/popup/popup.service";
import { CacheService } from "@common/service/cache.service";
import { IDocumentService } from "@common/service/document.service";
import { DocumentSignature } from "@common/model/documentSignaure";
import { IUtilityService } from "@common/service/util.service";
import { IAppRootScopeService } from "@/app/app.definition";
import { PDFJS_CONFIG } from "@/config/pdf.config";
import { SIGNATURE_FLOW } from "@common/model/constants";

interface IEcvPDFViewer {
    next($event): void;
    prev($event): void;
    first($event): void;
    last($event): void;
    renderPage(num): void;
    init(): void;
}

class EcvPDFViewer implements IEcvPDFViewer {
    document: any;
    container;
    pageWidth: number;
    pageHeight: number;
    type: string;
    currentPageNum: number;
    scale: number;
    totalPages: number;
    pageNumOptions: Array<number> = [];
    signatureItems: Array<object> = [];
    disableControls: boolean;
    onClose;
    onContinueSigning;
    signingInProgress;
    signingTitle;
    coverSheetDetails: any;
    pdfSinglePageViewer: any;

    static $inject = ['$rootScope', '$q', 'DocumentService', 'documentSignatureService', 'UtilityService', 'PopUpService', '$timeout',
        'CacheService', 'ApiAdapterService', 'storageConfig'];
    constructor(
        private $rootScope: IAppRootScopeService,
        private $q: ng.IQService,
        private $_document: IDocumentService,
        private $_signService: IDocumentSignatureService,
        private $_util: IUtilityService,
        private PopUpService: PopUpService,
        private $timeout: ng.ITimeoutService,
        private $_cache: CacheService,
        private $_api: JavaApiAdapterService,
        private storageConfig: any
    ) {
    }

    init() {
        this.scale = 1.0;
        this.totalPages = undefined;
        if (!this.currentPageNum) {
            this.currentPageNum = 1;
        }
        if (!!this.coverSheetDetails) {
            this.$rootScope.mainShowLoader = true;
            const bucketName = this.coverSheetDetails.bucketName, fileName = this.coverSheetDetails.fileName;

            const enablePrintPdf = (fileName, bucketName, sasToken) => {
                let pdfUrl = `${this.storageConfig.url}/${bucketName}/${fileName}?${sasToken}`;
                this.initPDFViewer(null, pdfUrl).then(() => {
                    this.container.addEventListener("pagesloaded", (e) => {
                        this.$_util.printPDF();
                    });
                    this.$rootScope.mainShowLoader = false;
                });
            };
            const sassObject = this.$_cache.getSASSToken();
            if (!!sassObject && !!sassObject.expiryTime && sassObject.expiryTime > new Date().toISOString()) {
                enablePrintPdf(fileName, bucketName, sassObject.sasToken);
            } else {
                this.$_api.getSASSToken().then((token) => {
                    this.$_cache.setSASSToken(token);
                    enablePrintPdf(fileName, bucketName, token.sasToken);
                });
            }
        }
        else if (!this.document || !this.document.content) {
            this.$rootScope.mainShowLoader = "Please wait while we fetch your document...";
            const isSigning = (this.$_signService.currentFlow == SIGNATURE_FLOW.SIGNING);
            this.$_document.getDocumentContent(this.document.id, (this.type == 'VIEW'), true, isSigning)
                .then((responseData) => {
                    const content = responseData.content;
                    this.initPDFViewer(content).then(() => {
                        this.$timeout(() => {
                            this.pageHeight = $(this.container).height();
                            this.pageWidth = $(this.container).width();

                            //scale PDF if required
                            const width = $(this.container).parents('.pdf-container').width();
                            if (width < this.pageWidth) {
                                this.scale = width / this.pageWidth;
                            }
                            this.pdfSinglePageViewer.currentScaleValue = this.scale;
                            const viewPort = this.pdfSinglePageViewer._pages[this.currentPageNum - 1].viewport;
                            this.pageHeight = viewPort.viewBox[3];
                            this.pageWidth = viewPort.viewBox[2];
                            // reset the scale to proper scale implemented
                            this.scale = viewPort.scale;
                            const data = [];
                            if (!isSigning && !!responseData.groups) {
                                responseData.groups.forEach(function (group) {
                                    group.objects.forEach(function (item) {
                                        item.color = group.color;
                                        group.pageNum = item.pageNum;
                                        data.push(new DocumentSignature().deserialize(item));
                                    });
                                    data.push(new DocumentSignature().deserialize(group));
                                });

                            }
                            if (isSigning) {
                                //here groups would be the linear array of objects
                                responseData.objects.forEach(function (item) {
                                    data.push(new DocumentSignature().deserialize(item));
                                });
                            }
                            this.$rootScope.$broadcast('SIGNATURE_LIST_FETCHED', data, this.totalPages);
                        }, 10);
                    }).catch((error) => {
                        this.PopUpService.openInformationModal(error);
                    });
                });
        }
    }

    initPDFViewer(data, url = '') {
        const deferred = this.$q.defer();
        this.container = document.getElementById('viewerContainer');

        const eventBus = new pdfjsViewer.EventBus();

        const pdfLinkService = new pdfjsViewer.PDFLinkService({
            eventBus: eventBus,
        });

        const pdfFindController = new pdfjsViewer.PDFFindController({
            linkService: pdfLinkService,
            eventBus: eventBus,
        });

        this.pdfSinglePageViewer = new pdfjsViewer.PDFSinglePageViewer({
            container: this.container,
            linkService: pdfLinkService,
            findController: pdfFindController,
            eventBus: eventBus,
        });

        pdfLinkService.setViewer(this.pdfSinglePageViewer);
        this.container.addEventListener('pagechange', (event) => {
            // handling of this event is required when page numbers changes due to
            // links within pdf for updating page number in current scope
            if (event.pageNumber != this.currentPageNum) {
                this.currentPageNum = event.pageNumber;
            }
        });
        let pdfjsLibReqObj = {
            cMapUrl: window[PDFJS_CONFIG].CMAP_URL,
            cMapPacked: window[PDFJS_CONFIG].CMAP_PACKED
        }
        if (!!data) {
            pdfjsLibReqObj['data'] = data;
        } else {
            pdfjsLibReqObj['url'] = url;
        }
        pdfjsLib.getDocument(pdfjsLibReqObj).promise.then((doc) => {
            this.totalPages = doc.numPages;
            this.resetOptions(this.totalPages);
            this.pdfSinglePageViewer.setDocument(doc);
            pdfLinkService.setDocument(doc, null);
            deferred.resolve();
        }).catch((e) => {
            deferred.reject()
        });
        return deferred.promise;
    }
    resetOptions(totalPages) {
        this.pageNumOptions = Array.from({ length: totalPages }, (v, k) => k + 1);
    }

    renderPage(num) {
        this.pdfSinglePageViewer.currentPageNumber = num;

    }
    next($event) {
        this.$_util.stopEventPropogation($event);
        if (this.currentPageNum >= this.totalPages) {
            return;
        }
        this.currentPageNum++;
    }

    prev($event) {
        this.$_util.stopEventPropogation($event);
        if (this.currentPageNum <= 1) {
            return;
        }
        this.currentPageNum--;
    }

    first($event) {
        this.$_util.stopEventPropogation($event);
        this.currentPageNum = 1;
    }

    last($event) {
        this.$_util.stopEventPropogation($event);
        this.currentPageNum = this.totalPages;
    }
    selectPage() {

    }
    stop($event) {
        this.$_util.stopEventPropogation($event);
    }
}

function export_func(): ng.IDirective {
    return {
        restrict: 'E',
        templateUrl: 'app/common/directive/pdf-viewer/templates/pdfViewer.directive.html',
        replace: true,
        scope: true,
        controller: EcvPDFViewer,
        controllerAs: 'pdfVm',
        bindToController: {
            'document': '=document',
            'type': '=type',
            'signatureItems': '=',
            'currentPageNum': '=',
            'disableControls': '=',
            'onClose': '&',
            'onContinueSigning': '&',
            'signingTitle': '=',
            'saveSignature': '&',
            'signingInProgress': '=',
            'coverSheetDetails': '=',
            'signClicked': '&'
        },
        link: (scope: ng.IScope, element: ng.IAugmentedJQuery, attributes: ng.IAttributes, controller: IEcvPDFViewer): void => {
            controller.init();
            scope.$watch('pdfVm.currentPageNum', (newValue, oldValue) => {
                if (newValue != oldValue) {
                    controller.renderPage(newValue);
                }
            });
            scope.$on('RELOAD_DOCUMENT', (event, data) => {
                controller.init();
            })
        }
    };
}

angular.module('eCareVault.common.directives').directive('ecvPdfViewer', export_func);
